import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { decodeData } from '../../../utils/encodeAndEncode';
import timeZones from '../../../utils/timeZone.json';
import {
  createZoomLiveClass,
  getZoomLiveClasses,
  updateZoomLiveClass,
  deleteZoomLiveClass,
  startZoomLiveClass,
  endZoomLiveClass
} from '../../../services/adminService';
import Loader from '../../../components/common/Loader';
import NoData from '../../../components/common/NoData';
import moment from 'moment-timezone';

const DefaultTimeZone = process.env.REACT_APP_DEFAULT_TIMEZONE || 'Asia/Kolkata';

function ClassroomLiveClasses() {
  const { classroomId: encodedClassroomId } = useParams();
  const classroomId = decodeData(encodedClassroomId);
  // console.log('decodedClassroomId------------------------------', classroomId);
  const userId = JSON.parse(localStorage.getItem('user'))?.id;

  // State management
  const [liveClasses, setLiveClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [formData, setFormData] = useState({
    live_class_name: '',
    points: '0',
    live_class_description: '',
    time_zone: DefaultTimeZone,
    class_date: '',
    start_time: '',
    duration: '30'
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Pagination and search state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Timezone dropdown state
  const [timeZoneSearch, setTimeZoneSearch] = useState('');
  const [isTimeZoneDropdownOpen, setIsTimeZoneDropdownOpen] = useState(false);

  // Loading states for action buttons
  const [startingClassId, setStartingClassId] = useState(null);
  const [endingClassId, setEndingClassId] = useState(null);

  // Debounced search query
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  // Debounce search query to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch live classes on component mount
  useEffect(() => {
    if (classroomId) {
      fetchLiveClasses();
    }
  }, [classroomId, currentPage, itemsPerPage, debouncedSearchQuery, statusFilter]);

  const fetchLiveClasses = async () => {
    try {
      setLoading(true);
      const response = await getZoomLiveClasses({
        classroom_id: classroomId,
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearchQuery,
        status: statusFilter
      });
      console.log('Live Classes Response:++++++++++++++++++++++++++++++++++', response);
      if (response.data) {
        setLiveClasses(response.data.live_classes || response.data || []);
        setTotalPages(response.data.pagination?.totalPages || 1);
        setTotalRecords(response.data.pagination?.totalRecords || 0);
        if (response.data.summary) {
          setSummary(response.data.summary);
        }
      }
    } catch (error) {
      console.error('Error fetching live classes:', error);
      toast.error('Failed to fetch live classes');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.live_class_name.trim()) {
      errors.live_class_name = 'Class name is required';
    }

    if (!formData.class_date) {
      errors.class_date = 'Class date is required';
    }

    if (!formData.start_time) {
      errors.start_time = 'Start time is required';
    }

    if (!formData.time_zone) {
      errors.time_zone = 'Time zone is required';
    }

    if (!formData.duration) {
      errors.duration = 'Duration is required';
    } else if (isNaN(formData.duration) || Number(formData.duration) < 1 || Number(formData.duration) > 480) {
      errors.duration = 'Duration must be between 1 and 480 minutes';
    }

    // Check if date and time are in the future
    if (formData.class_date && formData.start_time) {
      const selectedDateTime = new Date(`${formData.class_date}T${formData.start_time}`);
      const now = new Date();
      if (selectedDateTime <= now) {
        errors.start_time = 'Start time must be in the future';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      live_class_name: '',
      points: '0',
      live_class_description: '',
      time_zone: DefaultTimeZone,
      class_date: '',
      start_time: '',
      duration: '30'
    });
    setFormErrors({});
    setTimeZoneSearch('');
    setIsTimeZoneDropdownOpen(false);
  };

  const handleCreateClass = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);

      // Convert local time to UTC based on selected timezone
      const localDateTime = `${formData.class_date}T${formData.start_time}`;
      // console.log('Local DateTime Input:', localDateTime, 'Timezone:', formData.time_zone);
      const localMoment = moment.tz(localDateTime, formData.time_zone);
      // console.log('Local Moment:', localMoment.format());
      const utcDateTime = localMoment.utc().format('YYYY-MM-DD HH:mm:ss');
      // console.log('UTC DateTime for backend:', utcDateTime);


      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      const port = window.location.port;
      const fullURL = `${protocol}//${hostname}${port ? `:${port}` : ''}`;

      const payload = {
        classroom_id: classroomId,
        live_class_name: formData.live_class_name,
        earning_points: formData.points || 0,
        live_class_description: formData.live_class_description,
        time_zone: formData.time_zone,
        class_date: formData.class_date,
        start_time: localDateTime,
        duration: `${formData.duration} minutes`,
        sub_domain: fullURL,
        userId: userId
      };

      const response = await createZoomLiveClass(payload);
      // console.log('Response==========================:', response);

      toast.success('Live class created successfully!');
      setShowCreateModal(false);
      resetForm();
      fetchLiveClasses();

      // if (response.data.success) {
      //   toast.success('Live class created successfully!');
      //   setShowCreateModal(false);
      //   resetForm();
      //   fetchLiveClasses();
      // } else {
      //   toast.error(response.data.message || 'Failed to create live class');
      //   console.error('response', response.data);
      // }
    } catch (error) {
      console.error('Error creating live class:', error);
      toast.error('Failed to create live class');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClass = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);

      // Convert local time to UTC based on selected timezone
      const localDateTime = `${formData.class_date}T${formData.start_time}`;
      const localMoment = moment.tz(localDateTime, formData.time_zone);
      const utcDateTime = localMoment.utc().format('YYYY-MM-DD HH:mm:ss');

      const payload = {
        live_class_name: formData.live_class_name,
        earning_points: formData.points || 0,
        live_class_description: formData.live_class_description,
        time_zone: formData.time_zone,
        class_date: formData.class_date,
        start_time: localDateTime,
        duration: `${formData.duration} minutes`,
        userId: userId
      };

      const response = await updateZoomLiveClass(selectedClass.id, payload);

      // if (response.data.success) {
      toast.success('Live class updated successfully!');
      setShowEditModal(false);
      resetForm();
      setSelectedClass(null);
      fetchLiveClasses();
      // } else {
      // toast.error(response.data.message || 'Failed to update live class');
      // }
    } catch (error) {
      console.error('Error updating live class:', error);
      toast.error('Failed to update live class');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClass = async () => {
    try {
      setSubmitting(true);
      const response = await deleteZoomLiveClass(selectedClass.id);
      toast.success('Live class deleted successfully!');
      setShowDeleteModal(false);
      setSelectedClass(null);
      fetchLiveClasses();
    } catch (error) {
      console.error('Error deleting live class:', error);
      toast.error('Failed to delete live class');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartClass = async (liveClass) => {
    try {
      setStartingClassId(liveClass.id);

      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      const port = window.location.port;
      const fullURL = `${protocol}//${hostname}${port ? `:${port}` : ''}`;
      // Add minimum loading time of 1.5 seconds
      const startTime = Date.now();
      const response = await startZoomLiveClass({ live_class_id: liveClass.id, sub_domain: fullURL });
      // console.log('Response==========================:', response);
      const elapsedTime = Date.now() - startTime;

      // Ensure minimum 1.5 seconds loading time
      if (elapsedTime < 1500) {
        await new Promise(resolve => setTimeout(resolve, 1500 - elapsedTime));
      }

      toast.success('Live class started successfully!');
      // Open the host URL in the same tab
      window.location.href = response.data.host_url;
    } catch (error) {
      console.error('Error starting live class:', error);
      toast.error('Failed to start live class');
    } finally {
      setStartingClassId(null);
    }
  };

  const handleJoinClass = async (liveClass) => {
    try {

      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      const port = window.location.port;
      const fullURL = `${protocol}//${hostname}${port ? `:${port}` : ''}`;

      // For already started meetings, generate a fresh host URL
      const response = await startZoomLiveClass({ live_class_id: liveClass.id, sub_domain: fullURL });
      // console.log('Response==========================:', response);

      // if (response.data.success) {
      toast.success(response.data.message || 'Joining meeting...');
      // Open the host URL in the same tab
      window.location.href = response.data.host_url;
      // } else {
      // toast.error(response.data.message || 'Failed to join meeting');
      // }
    } catch (error) {

    }
  };

  const handleEndClass = async (liveClass) => {
    try {
      setEndingClassId(liveClass.id);

      // Add minimum loading time of 1.5 seconds
      const startTime = Date.now();
      const response = await endZoomLiveClass({ live_class_id: liveClass.id });
      const elapsedTime = Date.now() - startTime;

      // Ensure minimum 1.5 seconds loading time
      if (elapsedTime < 1500) {
        await new Promise(resolve => setTimeout(resolve, 1500 - elapsedTime));
      }

      // console.log('Response==========================:', response);
      toast.success('Live class ended successfully!');
      fetchLiveClasses();
    } catch (error) {
      console.error('Error ending live class:', error);
      toast.error('Failed to end live class');
    } finally {
      setEndingClassId(null);
    }
  };

  const openEditModal = (liveClass) => {
    setSelectedClass(liveClass);

    // Extract duration number from string (e.g., "60 minutes" -> "60")
    const durationMatch = liveClass.duration ? liveClass.duration.match(/(\d+)/) : null;
    const durationValue = durationMatch ? durationMatch[1] : '30';

    // Get the timezone from the live class or use default
    const timezone = liveClass.time_zone || DefaultTimeZone;

    // Convert UTC time to the selected timezone
    const utcMoment = moment.utc(liveClass.start_time);
    const localMoment = utcMoment.tz(timezone);

    const dateStr = localMoment.format('YYYY-MM-DD');
    const timeStr = localMoment.format('HH:mm');

    setFormData({
      live_class_name: liveClass.live_class_name || '',
      points: liveClass.earning_points !== undefined && liveClass.earning_points !== null ? liveClass.earning_points.toString() : '0',
      live_class_description: liveClass.live_class_description || '',
      time_zone: timezone,
      class_date: dateStr,
      start_time: timeStr,
      duration: durationValue
    });
    setTimeZoneSearch('');
    setIsTimeZoneDropdownOpen(false);
    setShowEditModal(true);
  };

  const openDeleteModal = (liveClass) => {
    setSelectedClass(liveClass);
    setShowDeleteModal(true);
  };

  // Search and pagination handlers
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when search changes
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleLimitChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1); // Reset to first page when limit changes
  };

  const getStatusBadge = (liveClass) => {
    const commonStyles = {
      fontWeight: '600',
      borderWidth: '2px',
      padding: '6px 12px',
      borderRadius: '8px',
      fontSize: '0.875rem'
    };

    if (liveClass.status === 'ended') {
      return (
        <span
          className="text-danger border border-danger"
          style={{ backgroundColor: '#fdeaea', ...commonStyles }}
        >
          Ended
        </span>
      );
    } else if (liveClass.status === 'started') {
      return (
        <span
          className="text-success border border-success"
          style={{ backgroundColor: '#e6f4ea', ...commonStyles }}
        >
          Live
        </span>
      );
    } else {
      return (
        <span
          className="text-primary border border-primary"
          style={{ backgroundColor: '#e9f1ff', ...commonStyles }}
        >
          Scheduled
        </span>
      );
    }
  };



  const formatDateTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return <Loader caption="Loading live classes..." />;
  }

  return (
    <div className="overflow-hidden">
      <div className="flex flex-nowrap items-center gap-3 px-0 py-0 border-b-0 bg-transparent overflow-x-auto whitespace-nowrap">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Icon icon="fluent:search-20-regular" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="search"
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#4F6BFF]/20 focus:border-[#4F6BFF]"
            placeholder="Search live classes..."
            value={searchQuery}
            onChange={handleSearchChange}
            aria-label="Search live classes"
          />
        </div>
        <select
          className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#4F6BFF]/20 min-w-[140px]"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
        >
          <option value="">All status</option>
          <option value="scheduled">Scheduled</option>
          <option value="live">Live</option>
          <option value="ended">Ended</option>
        </select>
        {(searchQuery || statusFilter) && (
          <button type="button" className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50" onClick={clearFilters} title="Clear filters">
            <Icon icon="mdi:close" width="18" height="18" />
          </button>
        )}
        <button
          type="button"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#4F6BFF] hover:bg-[#4358d4] text-white font-medium text-sm shrink-0"
          onClick={() => setShowCreateModal(true)}
        >
          <Icon icon="fluent:add-24-regular" width="18" height="18" />
          Create Live Class
        </button>

        <div className="flex items-center gap-1.5 ml-auto">
          <span className="text-sm text-gray-500">
            {totalRecords} live class{totalRecords !== 1 ? 'es' : ''}
          </span>
          {summary && (
            <>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-[#4F6BFF]/10 text-[#4F6BFF] text-[11px] font-semibold leading-none">
                Live: {summary?.live_count || 0}
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-50 text-blue-600 text-[11px] font-semibold leading-none">
                Upcoming: {summary?.scheduled_count || 0}
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-red-50 text-red-600 text-[11px] font-semibold leading-none">
                Ended: {summary?.ended_count || 0}
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-50 text-amber-700 text-[11px] font-semibold leading-none">
                Unattended: {summary?.unattended_count || 0}
              </span>
            </>
          )}
        </div>
      </div>

      <div className="overflow-x-auto max-h-[70vh] overflow-y-auto mt-4">
        {loading ? (
          <div className="flex items-center justify-center min-h-[320px]"><Loader caption="Loading live classes..." /></div>
        ) : (
          <table className="w-full text-sm" style={{ minWidth: '800px' }}>
            <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
              <tr>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-16">#</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Class name</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider max-w-[200px]">Description</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-40">Date & time</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">Created by</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">Status</th>
                <th className="px-4 py-3.5 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-36">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {liveClasses.length > 0 ? liveClasses.map((liveClass, index) => (
                <tr key={liveClass.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 align-middle text-gray-600">{((currentPage - 1) * itemsPerPage) + index + 1}</td>
                  <td className="px-4 py-3 align-middle font-medium text-gray-900">{liveClass.class_name}</td>
                  <td className="px-4 py-3 align-middle text-gray-500 max-w-[260px]" title={liveClass.class_desc || ''}>
                    <div className="line-clamp-2 leading-snug">
                      {liveClass.class_desc || '—'}
                    </div>
                  </td>
                  <td className="px-4 py-3 align-middle text-gray-600">
                    <span className="inline-flex items-center gap-1.5">
                      <Icon icon="fluent:calendar-24-regular" width="16" height="16" className="text-gray-400" />
                      {moment.utc(liveClass.start_time).local().format('DD MMM YYYY, hh:mm A')}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-middle text-gray-600">{liveClass.created_by_name || '—'}</td>
                  <td className="px-4 py-3 align-middle">{getStatusBadge(liveClass)}</td>
                  <td className="px-4 py-3 align-middle">
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                            {liveClass.is_ended === 1 ? (
                              <>
                                <span className="text-gray-500 text-sm flex items-center">Ended</span>
                                <button
                                  type="button"
                                  className="inline-flex items-center justify-center shrink-0 w-9 h-9 rounded-lg border border-red-500 text-red-500 hover:bg-red-50"
                                  onClick={() => openDeleteModal(liveClass)}
                                  title="Delete Class"
                                >
                                  <Icon icon="mdi:delete" width="18" height="18" />
                                </button>
                              </>
                            ) : liveClass.status === 'started' ? (
                              <>
                                <button
                                  type="button"
                                  className="inline-flex items-center justify-center shrink-0 w-9 h-9 rounded-lg border border-green-500 text-green-500 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                  onClick={() => handleJoinClass(liveClass)}
                                  title="Join Meeting"
                                >
                                  <Icon icon="mdi:video" width="18" height="18" />
                                </button>
                                <button
                                  type="button"
                                  className="inline-flex items-center justify-center shrink-0 w-9 h-9 rounded-lg border border-red-500 text-red-500 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                  onClick={() => handleEndClass(liveClass)}
                                  title="End Meeting"
                                  disabled={endingClassId === liveClass.id}
                                >
                                  {endingClassId === liveClass.id ? (
                                    <span className="inline-block w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <Icon icon="mdi:stop" width="18" height="18" />
                                  )}
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="inline-flex items-center justify-center shrink-0 w-9 h-9 rounded-lg border border-[#4F6BFF] text-[#4F6BFF] hover:bg-[#4F6BFF]/10 disabled:opacity-50 disabled:cursor-not-allowed"
                                  onClick={() => handleStartClass(liveClass)}
                                  title="Start Meeting"
                                  disabled={startingClassId === liveClass.id}
                                >
                                  {startingClassId === liveClass.id ? (
                                    <span className="inline-block w-4 h-4 border-2 border-[#4F6BFF] border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <Icon icon="mdi:play" width="18" height="18" />
                                  )}
                                </button>
                                <button
                                  type="button"
                                  className="inline-flex items-center justify-center shrink-0 w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"
                                  onClick={() => openEditModal(liveClass)}
                                  title="Edit Class"
                                >
                                  <Icon icon="mdi:pencil" width="18" height="18" />
                                </button>
                                <button
                                  type="button"
                                  className="inline-flex items-center justify-center shrink-0 w-9 h-9 rounded-lg border border-red-500 text-red-500 hover:bg-red-50"
                                  onClick={() => openDeleteModal(liveClass)}
                                  title="Delete Class"
                                >
                                  <Icon icon="mdi:delete" width="18" height="18" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={7} className="px-4 py-16 text-center">
                          <NoData
                            title="No Live Classes Found"
                            description="Create your first live class to get started with Zoom meetings."
                          />
                        </td>
                      </tr>
                    )}
            </tbody>
          </table>
        )}
      </div>

      {!loading && (
        <div className="mt-6 pt-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4 px-4 pb-4">
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <span className="hidden sm:inline">Rows per page:</span>
            <select
              className="px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F6BFF]/20 text-sm"
              value={itemsPerPage}
              onChange={handleLimitChange}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="text-gray-500">
              {totalRecords === 0 ? 'No records' : `Showing ${(currentPage - 1) * itemsPerPage + 1}–${Math.min(currentPage * itemsPerPage, totalRecords)} of ${totalRecords}`}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <button
              type="button"
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || totalPages === 0}
              title="Previous page"
            >
              <Icon icon="fluent:chevron-left-24-regular" width={16} height={16} />
            </button>
            <span className="px-2 text-gray-700">
              Page <span className="font-medium">{totalPages === 0 ? 0 : currentPage}</span> of <span className="font-medium">{totalPages || 0}</span>
            </span>
            <button
              type="button"
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              title="Next page"
            >
              <Icon icon="fluent:chevron-right-24-regular" width={16} height={16} />
            </button>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-auto">
            <div className="modal-content">
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
                <h5 className="font-semibold text-lg">Create Live Class</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                ></button>
              </div>
              <form onSubmit={handleCreateClass}>
                <div className="p-6">
                  <div className="flex flex-col">
                    <div className="w-full mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Class Name *</label>
                      <input
                        type="text"
                        className={`w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.live_class_name ? 'border-red-500' : ''}`}
                        name="live_class_name"
                        value={formData.live_class_name}
                        onChange={handleInputChange}
                        placeholder="Enter class name"
                      />
                      {formErrors.live_class_name && (
                        <div className="text-red-500 text-sm mt-1">{formErrors.live_class_name}</div>
                      )}
                    </div>

                    <div className="w-full mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Points</label>
                      <input
                        type="number"
                        className={`w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.points ? 'border-red-500' : ''}`}
                        name="points"
                        value={formData.points}
                        onChange={handleInputChange}
                        placeholder="0"
                        min="0"
                      />
                      <small className="text-gray-500 text-xs">
                        Reward points trainees receive after successfully attending this live class.
                      </small>
                      {formErrors.points && (
                        <div className="text-red-500 text-sm mt-1">{formErrors.points}</div>
                      )}
                    </div>

                    <div className="w-full mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                      <textarea
                        className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                        name="live_class_description"
                        value={formData.live_class_description}
                        onChange={handleInputChange}
                        rows="3"
                        placeholder="Enter class description (optional)"
                      ></textarea>
                    </div>

                    <div className="w-full mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Time Zone <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <div
                          className={`w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 flex items-center justify-between ${formErrors.time_zone ? 'border-red-500' : ''}`}
                          onClick={() => setIsTimeZoneDropdownOpen(!isTimeZoneDropdownOpen)}
                          style={{
                            borderRadius: '8px',
                            border: formErrors.time_zone ? '1px solid #dc3545' : '1px solid #e0e0e0',
                            padding: '12px 16px',
                            fontSize: '0.875rem',
                            backgroundColor: '#fff',
                            cursor: 'pointer'
                          }}
                        >
                          <span>{formData.time_zone ? timeZones.find(zone => zone.value === formData.time_zone)?.label : 'Select Time Zone'}</span>
                          <Icon icon={isTimeZoneDropdownOpen ? "eva:arrow-up-fill" : "eva:arrow-down-fill"} />
                        </div>
                        {isTimeZoneDropdownOpen && (
                          <div
                            className="absolute w-full bg-white border rounded-xl mt-1 shadow-sm"
                            style={{
                              maxHeight: '300px',
                              overflowY: 'auto',
                              zIndex: 1000
                            }}
                          >
                            <div className="p-2 sticky top-0 bg-white">
                              <div className="relative">
                                <input
                                  type="text"
                                  className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                                  placeholder="Search timezone..."
                                  value={timeZoneSearch}
                                  onChange={(e) => setTimeZoneSearch(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    paddingLeft: '35px',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '6px',
                                    fontSize: '0.875rem'
                                  }}
                                />
                                <Icon
                                  icon="eva:search-outline"
                                  style={{
                                    position: 'absolute',
                                    left: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#6c757d'
                                  }}
                                />
                              </div>
                            </div>
                            <div className="timezone-options">
                              {timeZones
                                .filter(zone =>
                                  zone.label.toLowerCase().includes(timeZoneSearch.toLowerCase()) ||
                                  zone.value.toLowerCase().includes(timeZoneSearch.toLowerCase())
                                )
                                .map((zone) => (
                                  <div
                                    key={zone.value}
                                    className="timezone-option"
                                    onClick={() => {
                                      setFormData(prev => ({ ...prev, time_zone: zone.value }));
                                      setIsTimeZoneDropdownOpen(false);
                                      setTimeZoneSearch('');
                                    }}
                                    style={{
                                      padding: '10px 16px',
                                      cursor: 'pointer',
                                      backgroundColor: formData.time_zone === zone.value ? '#f8f9fa' : 'transparent',
                                      transition: 'background-color 0.2s',
                                      fontSize: '0.875rem',
                                      color: '#333'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = formData.time_zone === zone.value ? '#f8f9fa' : 'transparent'}
                                  >
                                    {zone.label}
                                  </div>
                                ))
                              }
                            </div>
                          </div>
                        )}
                      </div>
                      {formErrors.time_zone && (
                        <div className="text-red-500 text-sm mt-1">{formErrors.time_zone}</div>
                      )}
                    </div>

                    <div className="md:w-1/2 mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Date *</label>
                      <input
                        type="date"
                        className={`w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.class_date ? 'border-red-500' : ''}`}
                        name="class_date"
                        value={formData.class_date}
                        onChange={handleInputChange}
                        min={new Date().toISOString().split('T')[0]}
                      />
                      {formErrors.class_date && (
                        <div className="text-red-500 text-sm mt-1">{formErrors.class_date}</div>
                      )}
                    </div>

                    <div className="md:w-1/2 mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Start Time *</label>
                      <input
                        type="time"
                        className={`w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.start_time ? 'border-red-500' : ''}`}
                        name="start_time"
                        value={formData.start_time}
                        onChange={handleInputChange}
                      />
                      {formErrors.start_time && (
                        <div className="text-red-500 text-sm mt-1">{formErrors.start_time}</div>
                      )}
                    </div>

                    <div className="w-full mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Duration (minutes) <span className="text-red-500">*</span></label>
                      <input
                        type="number"
                        className={`w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.duration ? 'border-red-500' : ''}`}
                        name="duration"
                        value={formData.duration}
                        onChange={handleInputChange}
                        placeholder="Enter duration in minutes"
                        min="1"
                        max="480"
                      />
                      {formErrors.duration && (
                        <div className="text-red-500 text-sm mt-1">{formErrors.duration}</div>
                      )}
                      <div className="text-gray-500 text-sm mt-1">
                        Enter the duration in minutes (1-480 minutes allowed)
                      </div>
                    </div>


                  </div>
                </div>
                <div className="px-6 py-4 border-t flex justify-end gap-5 sticky bottom-0 bg-white z-10">
                  <button
                    type="button"
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg w-full"
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg w-full"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                        Creating...
                      </>
                    ) : (
                      'Create Live Class'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-w-2xl">
            <div className="modal-content">
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                <h5 className="font-semibold text-lg">Edit Live Class</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                    setSelectedClass(null);
                  }}
                ></button>
              </div>
              <form onSubmit={handleEditClass}>
                <div className="p-6">
                  {selectedClass?.is_started ? (
                    <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg mb-3">
                      <Icon icon="mdi:information" className="mr-2" />
                      Meeting has started. Only description can be edited.
                    </div>
                  ) : null}

                  <div className="flex flex-col">
                    <div className="w-full mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Class Name *</label>
                      <input
                        type="text"
                        className={`w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.live_class_name ? 'border-red-500' : ''}`}
                        name="live_class_name"
                        value={formData.live_class_name}
                        onChange={handleInputChange}
                        disabled={selectedClass?.status === 'started'}
                        placeholder="Enter class name"
                      />
                      {formErrors.live_class_name && (
                        <div className="text-red-500 text-sm mt-1">{formErrors.live_class_name}</div>
                      )}
                    </div>

                    <div className="w-full mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Points</label>
                      <input
                        type="number"
                        className={`w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.points ? 'border-red-500' : ''}`}
                        name="points"
                        value={formData.points}
                        onChange={handleInputChange}
                        disabled={selectedClass?.status === 'started'}
                        placeholder="Enter points"
                        min="0"
                      />
                      <small className="text-gray-500">Trainees will get these points once they attend this live class</small>
                      {formErrors.points && (
                        <div className="text-red-500 text-sm mt-1">{formErrors.points}</div>
                      )}
                    </div>

                    <div className="w-full mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                      <textarea
                        className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                        name="live_class_description"
                        value={formData.live_class_description}
                        onChange={handleInputChange}
                        rows="3"
                        placeholder="Enter class description (optional)"
                      ></textarea>
                    </div>

                    <div className="w-full mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Time Zone <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <div
                          className={`w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 flex items-center justify-between ${formErrors.time_zone ? 'border-red-500' : ''}`}
                          onClick={() => setIsTimeZoneDropdownOpen(!isTimeZoneDropdownOpen)}
                          style={{
                            borderRadius: '8px',
                            border: formErrors.time_zone ? '1px solid #dc3545' : '1px solid #e0e0e0',
                            padding: '12px 16px',
                            fontSize: '0.875rem',
                            backgroundColor: '#fff',
                            cursor: 'pointer'
                          }}
                        >
                          <span>{formData.time_zone ? timeZones.find(zone => zone.value === formData.time_zone)?.label : 'Select Time Zone'}</span>
                          <Icon icon={isTimeZoneDropdownOpen ? "eva:arrow-up-fill" : "eva:arrow-down-fill"} />
                        </div>
                        {isTimeZoneDropdownOpen && (
                          <div
                            className="absolute w-full bg-white border rounded-xl mt-1 shadow-sm"
                            style={{
                              maxHeight: '300px',
                              overflowY: 'auto',
                              zIndex: 1000
                            }}
                          >
                            <div className="p-2 sticky top-0 bg-white">
                              <div className="relative">
                                <input
                                  type="text"
                                  className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                                  placeholder="Search timezone..."
                                  value={timeZoneSearch}
                                  onChange={(e) => setTimeZoneSearch(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    paddingLeft: '35px',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '6px',
                                    fontSize: '0.875rem'
                                  }}
                                />
                                <Icon
                                  icon="eva:search-outline"
                                  style={{
                                    position: 'absolute',
                                    left: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#6c757d'
                                  }}
                                />
                              </div>
                            </div>
                            <div className="timezone-options">
                              {timeZones
                                .filter(zone =>
                                  zone.label.toLowerCase().includes(timeZoneSearch.toLowerCase()) ||
                                  zone.value.toLowerCase().includes(timeZoneSearch.toLowerCase())
                                )
                                .map((zone) => (
                                  <div
                                    key={zone.value}
                                    className="timezone-option"
                                    onClick={() => {
                                      setFormData(prev => ({ ...prev, time_zone: zone.value }));
                                      setIsTimeZoneDropdownOpen(false);
                                      setTimeZoneSearch('');
                                    }}
                                    style={{
                                      padding: '10px 16px',
                                      cursor: 'pointer',
                                      backgroundColor: formData.time_zone === zone.value ? '#f8f9fa' : 'transparent',
                                      transition: 'background-color 0.2s',
                                      fontSize: '0.875rem',
                                      color: '#333'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = formData.time_zone === zone.value ? '#f8f9fa' : 'transparent'}
                                  >
                                    {zone.label}
                                  </div>
                                ))
                              }
                            </div>
                          </div>
                        )}
                      </div>
                      {formErrors.time_zone && (
                        <div className="text-red-500 text-sm mt-1">{formErrors.time_zone}</div>
                      )}
                    </div>


                    {!selectedClass?.status === 'started' && (
                      <>
                        <div className="md:w-1/2 mb-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Date *</label>
                          <input
                            type="date"
                            className={`w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.class_date ? 'border-red-500' : ''}`}
                            name="class_date"
                            value={formData.class_date}
                            onChange={handleInputChange}
                            min={new Date().toISOString().split('T')[0]}
                          />
                          {formErrors.class_date && (
                            <div className="text-red-500 text-sm mt-1">{formErrors.class_date}</div>
                          )}
                        </div>

                        <div className="md:w-1/2 mb-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Start Time *</label>
                          <input
                            type="time"
                            className={`w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.start_time ? 'border-red-500' : ''}`}
                            name="start_time"
                            value={formData.start_time}
                            onChange={handleInputChange}
                          />
                          {formErrors.start_time && (
                            <div className="text-red-500 text-sm mt-1">{formErrors.start_time}</div>
                          )}
                        </div>

                        <div className="w-full mb-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Duration (minutes) <span className="text-red-500">*</span></label>
                          <input
                            type="number"
                            className={`w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.duration ? 'border-red-500' : ''}`}
                            name="duration"
                            value={formData.duration}
                            onChange={handleInputChange}
                            placeholder="Enter duration in minutes"
                            min="1"
                            max="480"
                          />
                          {formErrors.duration && (
                            <div className="text-red-500 text-sm mt-1">{formErrors.duration}</div>
                          )}
                          <div className="text-gray-500 text-sm mt-1">
                            Enter the duration in minutes (1-480 minutes allowed)
                          </div>
                        </div>


                      </>
                    )}
                  </div>
                </div>
                <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                  <button
                    type="button"
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg"
                    onClick={() => {
                      setShowEditModal(false);
                      resetForm();
                      setSelectedClass(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                        Updating...
                      </>
                    ) : (
                      'Update Live Class'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
            <div className="modal-content">
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                <h5 className="font-semibold text-lg">Delete Live Class</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedClass(null);
                  }}
                ></button>
              </div>
              <div className="p-6">
                <div className="text-center">
                  <Icon icon="mdi:alert-circle" className="text-red-500 mb-3" style={{ fontSize: '48px' }} />
                  <h6 className="font-semibold mb-2">Are you sure you want to delete this live class?</h6>
                  <p className="text-gray-500 mb-2">
                    <strong>{selectedClass?.live_class_name}</strong>
                  </p>
                  <p className="text-gray-500 text-sm">
                    This action cannot be undone. The Zoom meeting will also be deleted.
                  </p>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                <button
                  type="button"
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedClass(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-lg"
                  onClick={handleDeleteClass}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                      Deleting...
                    </>
                  ) : (
                    'Delete Live Class'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClassroomLiveClasses;
